import {
  Briefcase,
  Building2,
  Calendar,
  ClipboardList,
  CreditCard,
  DollarSign,
  FileText,
  Handshake,
  HeartHandshake,
  History,
  KeyRound,
  LockKeyhole,
  Megaphone,
  MessageSquare,
  ShieldCheck,
  ShoppingCart,
  Timer,
  Users,
  type LucideIcon,
} from 'lucide-react'

// Static copy for the promotional landing. Every claim here must describe something the product
// already does; roadmap items say so. No pricing and no specific sectors: keep audiences general.

export interface LandingItem {
  readonly title: string
  readonly description: string
  readonly icon: LucideIcon
}

export const AUDIENCES: readonly string[] = [
  'Asociaciones y corporaciones',
  'Fundaciones y ONG',
  'Clubes deportivos, sociales y culturales',
  'Comunidades y agrupaciones',
]

export type ShowcaseKey = 'dues' | 'announcements' | 'events' | 'finance'

export interface ShowcaseItem {
  readonly key: ShowcaseKey
  readonly eyebrow: string
  readonly title: string
  readonly description: string
  readonly points: readonly string[]
}

export const SHOWCASE: readonly ShowcaseItem[] = [
  {
    key: 'dues',
    eyebrow: 'Cuotas',
    title: 'Cada miembro ve y paga su cuota desde el celular',
    description:
      'La tesorería registra transferencias y pagos en efectivo, y los pagos en línea se confirman solos cuando Mercado Pago los aprueba.',
    points: [
      'Estado mensual de cada miembro',
      'Pago de varios meses en una sola operación',
      'Cada pago queda registrado en Finanzas',
    ],
  },
  {
    key: 'announcements',
    eyebrow: 'Comunicados',
    title: 'La información oficial no se pierde entre mensajes',
    description:
      'Publica avisos para toda la organización en un espacio propio, separado de los chats personales.',
    points: [
      'Un solo lugar para la información oficial',
      'Historial ordenado y siempre disponible',
      'Solo lo ven los miembros de tu organización',
    ],
  },
  {
    key: 'events',
    eyebrow: 'Actividades',
    title: 'Reuniones, talleres y encuentros con inscripción',
    description: 'Publica la actividad, define los cupos y deja que los miembros se inscriban en un clic.',
    points: ['Vistas de lista, semana y mes', 'Cupos e inscripciones en línea', 'Lista de asistentes para la organización'],
  },
  {
    key: 'finance',
    eyebrow: 'Finanzas',
    title: 'Cuentas claras para la directiva y los miembros',
    description:
      'Ingresos y egresos ordenados por categoría, con controles para que ningún gasto se apruebe solo.',
    points: [
      'Ingresos y egresos por categoría',
      'Cada egreso lo aprueba una persona distinta a quien lo registra',
      'Cuotas, aportes y ventas se registran automáticamente',
    ],
  },
]

export const MODULES: readonly LandingItem[] = [
  { title: 'Comunicados', description: 'Avisos oficiales para todos los miembros en un espacio propio.', icon: Megaphone },
  { title: 'Miembros', description: 'Directorio, cargos, roles e incorporación solo por invitación.', icon: Users },
  { title: 'Cuotas', description: 'Control mensual por miembro, exenciones y pago en línea con registro automático en Finanzas.', icon: CreditCard },
  { title: 'Finanzas', description: 'Ingresos, egresos con aprobación separada, categorías y balance.', icon: DollarSign },
  { title: 'Campañas', description: 'Recaudación con meta y aportes pagados con Mercado Pago.', icon: HeartHandshake },
  { title: 'Actividades', description: 'Calendario, inscripciones con cupos y lista de asistentes.', icon: Calendar },
  { title: 'Chat', description: 'Canales de conversación dentro de la organización, separados de los chats personales.', icon: MessageSquare },
  { title: 'Documentos', description: 'Biblioteca de actas, reglamentos y archivos con acceso según permisos.', icon: FileText },
  { title: 'Convenios', description: 'Beneficios y alianzas disponibles para los miembros.', icon: Handshake },
  { title: 'Ventas e insumos', description: 'Catálogo con límite por miembro, reservas y pago en línea.', icon: ShoppingCart },
  { title: 'Proyectos', description: 'Seguimiento de iniciativas con estado, prioridad y avance.', icon: Briefcase },
  { title: 'Solicitudes', description: 'Recepción y seguimiento de requerimientos de los miembros.', icon: ClipboardList },
]

export const STEPS: readonly { readonly title: string; readonly description: string }[] = [
  {
    title: 'Creamos tu espacio',
    description: 'Zetyar habilita la organización con los módulos que necesita y su primer administrador.',
  },
  {
    title: 'Invitas a tus miembros',
    description: 'La administración envía invitaciones por correo y asigna roles como tesorería o comunicaciones.',
  },
  {
    title: 'La comunidad participa',
    description: 'Los miembros se informan, se inscriben en actividades y pagan sus cuotas desde el celular o el computador.',
  },
]

export const SECURITY_POINTS: readonly LandingItem[] = [
  {
    title: 'Aislamiento por organización',
    description: 'Cada consulta se autoriza en la base de datos con Row Level Security. Una organización nunca ve los datos de otra.',
    icon: Building2,
  },
  {
    title: 'Roles y permisos definidos',
    description: 'Administración, tesorería, revisión financiera, moderación, proyectos, miembros e invitados, cada uno con lo justo.',
    icon: KeyRound,
  },
  {
    title: 'Pagos confirmados en el servidor',
    description: 'Mercado Pago confirma cada pago con una notificación firmada. Ningún pago se da por hecho desde el navegador.',
    icon: ShieldCheck,
  },
  {
    title: 'Auditoría de acciones sensibles',
    description: 'Cambios de roles, movimientos financieros y registros de pago quedan trazados.',
    icon: History,
  },
  {
    title: 'Sesiones protegidas',
    description: 'Reautenticación para acciones sensibles y cierre automático por inactividad en la administración.',
    icon: Timer,
  },
  {
    title: 'Protección de datos desde el diseño',
    description: 'Datos mínimos por organización y trazabilidad, en preparación para la Ley 21.719.',
    icon: LockKeyhole,
  },
]

export const FAQS: readonly { readonly question: string; readonly answer: string }[] = [
  {
    question: '¿Una persona puede pertenecer a más de una organización?',
    answer:
      'Sí. Cada persona usa una sola cuenta y cambia de organización desde la misma sesión. Ninguna organización puede ver las otras membresías de esa persona.',
  },
  {
    question: '¿Cómo se incorporan los miembros?',
    answer:
      'Solo por invitación. La administración de la organización envía la invitación por correo y define el rol de cada persona.',
  },
  {
    question: '¿Los miembros pueden pagar en línea?',
    answer:
      'Sí. Cada organización conecta su propia cuenta de Mercado Pago, así los pagos de cuotas, campañas y ventas llegan directo a la organización y se registran en Finanzas cuando Mercado Pago los aprueba.',
  },
  {
    question: '¿Puedo elegir qué módulos usar?',
    answer:
      'Sí. Cada organización activa solo los módulos que necesita, y los miembros ven únicamente lo que corresponde a su rol.',
  },
  {
    question: '¿Hay aplicación móvil?',
    answer:
      'Hoy todo funciona desde el navegador, también en el celular. La aplicación móvil para miembros está en desarrollo.',
  },
  {
    question: '¿Dónde se guardan los datos?',
    answer:
      'En una base de datos PostgreSQL administrada en Sudamérica, con cada organización aislada por políticas de seguridad en el propio motor de datos.',
  },
]
